import { Component, EnvironmentInjector, OnDestroy, OnInit, runInInjectionContext, signal, inject } from '@angular/core';
import { AppDataService } from 'src/app/shared/services/app-data.service';
import { Address, ChargingStation, Connector, Location, LocationDataRoot, MapLocation } from '../ok-app-emulator.model';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FirestoreService } from 'src/app/shared/services/firestore.service';
import { BehaviorSubject, catchError, forkJoin, Observable, of, Subscription, take } from 'rxjs';
import {
  AppVisibilityStatus,
  ConnectorStatus,
  FirestoreConnectorInfo,
} from '../../charging-station-location/charging-station-location-model';
import { AddressService } from 'src/app/core/services/address.service';
import { Address as CommonAddressModel } from 'src/app/shared/models/address';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { AccessGroup } from 'src/app/features/access-groups/access-group.model';
import { RouterLink } from '@angular/router';
import { FeedbackInfoComponent } from 'ok-angular-shared/components';
import { DecimalPipe, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger, MatMenuItem } from '@angular/material/menu';
import { MatDialogClose } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'adm-location-details-in-map',
  templateUrl: './location-details-in-map.component.html',
  styleUrls: ['./location-details-in-map.component.scss'],
  imports: [
    RouterLink,
    FeedbackInfoComponent,
    DecimalPipe,
    AsyncPipe,
    MatButton,
    MatIcon,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    MatDialogClose,
    MatTooltip,
  ],
})
export class LocationDetailsInMapComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<LocationDetailsInMapComponent>>(MatDialogRef);
  data = inject<{
    location: MapLocation;
    customerNumbers: number[];
    mokUserNumber: number;
    deviceId: string;
    version: string;
  }>(MAT_DIALOG_DATA);
  private appDataService = inject(AppDataService);
  private accessGroupService = inject(AccessGroupService);
  private firestoreService = inject(FirestoreService);
  private addressService = inject(AddressService);
  private environmentInjector = inject(EnvironmentInjector);
  public location = signal<Location | undefined>(undefined);
  public chargingStations = signal<ChargingStation[]>([]);
  public connectorStatusChangedSubscription: Subscription;
  private subscriptions: Array<Subscription> = [];
  public customerNumbers: number[] = [];
  public mokUserNumber: number | null = null;
  public deviceId: string | null = null;
  public locationId = '';
  public version = 'v1';
  public endpointUrl: string | null = null;
  public showRestrictedInfo = false;
  public connectorStatusEnum: typeof ConnectorStatus = ConnectorStatus;
  public connectorStatusChanged$: Observable<FirestoreConnectorInfo[]>;
  public firestoreConnectorInfoItems: FirestoreConnectorInfo[] = [];
  ngOnDestroy(): void {
    runInInjectionContext(this.environmentInjector, () => {
      this.firestoreService.tearDownFireStoreConnectorSubscriptions();
    });
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
    this.subscriptions = [];
  }
  ngOnInit() {
    runInInjectionContext(this.environmentInjector, async () => {
      this.customerNumbers = this.data.customerNumbers;
      this.deviceId = this.data.deviceId;
      this.mokUserNumber = this.data.mokUserNumber;
      this.locationId = this.data.location.locationId;
      this.version = this.data.version;
      this.showRestrictedInfo = this.customerNumbers?.length > 0;

      this.firestoreService.setupFireStoreConnectorSubscription(this.locationId);
      this.connectorStatusChanged$ = this.firestoreService.connectorStatusChanged$();

      // Load immediately instead of waiting for a Firestore push, which may never arrive
      if (this.locationId) {
        this.getLocationDetails(this.locationId);
      }

      this.connectorStatusChangedSubscription = this.connectorStatusChanged$.subscribe(connectors => {
        connectors.forEach(fsc => {
          this.firestoreConnectorInfoItems.push(fsc);
        });
        if (this.locationId) {
          this.getLocationDetails(this.locationId);
        }
      });
      this.subscriptions.push(this.connectorStatusChangedSubscription);
    });
  }

  public async getLocationDetails(locationId: string) {
    if (this.version === 'v3') {
      this.endpointUrl = this.appDataService.getLocationDetailsApiRequestUrlV3(
        locationId,
        this.mokUserNumber,
        this.customerNumbers,
        this.deviceId
      );
    } else if (this.version === 'v4') {
      this.endpointUrl = this.appDataService.getLocationDetailsApiRequestUrlV4(
        locationId,
        this.mokUserNumber,
        this.customerNumbers,
        this.deviceId
      );
    } else {
      this.endpointUrl = this.appDataService.getLocationDetailsApiRequestUrlV1(locationId);
    }
    this.appDataService
      .getLocationDetails(locationId, this.mokUserNumber, this.customerNumbers, this.version, this.deviceId)
      .pipe(take(1))
      .subscribe({
        next: (resp: LocationDataRoot) => {
          const location = resp.locationData;
          location.formattedAddress = this.formatAddressToString(location.address);

          const chargingStations = location.chargingStations;
          chargingStations.forEach(chargingStation => {
            chargingStation.connectors = chargingStation.connectors.sort(function (a: Connector, b: Connector) {
              return a.connectorId - b.connectorId;
            });

            for (let index = 0; index < chargingStation.connectors.length; index++) {
              const connector = chargingStation.connectors[index];
              connector.isAccessGroupRestricted = location.visibility === AppVisibilityStatus.Restrictions;
              connector.firestoreConnectorInfo = new BehaviorSubject<FirestoreConnectorInfo>({
                connectorStatus: ConnectorStatus.Unknown,
              } as FirestoreConnectorInfo);
            }
            for (const fsc of this.firestoreConnectorInfoItems) {
              const matchingConnector = chargingStation.connectors.find(
                c => c.chargingStationId === fsc.chargingStationIdentifier && c.connectorId === fsc.connectorNumber
              );
              if (matchingConnector) {
                matchingConnector.firestoreConnectorInfo = new BehaviorSubject<FirestoreConnectorInfo>(fsc);
              }
            }
          });

          this.location.set(location);
          this.chargingStations.set(chargingStations);
          this.loadAccessGroupDetails(chargingStations);
        },
      });
  }

  private loadAccessGroupDetails(chargingStations: ChargingStation[]): void {
    const connectors = chargingStations.flatMap(chargingStation => chargingStation.connectors);
    const accessGroupIdentifiers = [
      ...new Set(
        connectors
          .map(connector => connector.accessGroupIdentifier)
          .filter((identifier): identifier is string => !!identifier)
      ),
    ];

    if (accessGroupIdentifiers.length === 0) {
      return;
    }

    forkJoin(
      accessGroupIdentifiers.map(identifier =>
        this.accessGroupService.getAccessGroup(identifier).pipe(catchError(() => of(null)))
      )
    )
      .pipe(take(1))
      .subscribe(accessGroups => {
        const accessGroupsByIdentifier = new Map<string, AccessGroup>();
        accessGroups.forEach(accessGroup => {
          if (accessGroup) {
            accessGroupsByIdentifier.set(accessGroup.identifier, accessGroup);
          }
        });

        const enrichedChargingStations = chargingStations.map(chargingStation => ({
          ...chargingStation,
          connectors: chargingStation.connectors.map(connector => {
            const accessGroup = accessGroupsByIdentifier.get(connector.accessGroupIdentifier);
            return accessGroup
              ? {
                  ...connector,
                  accessGroupName: accessGroup.name,
                }
              : connector;
          }),
        }));

        this.chargingStations.set(enrichedChargingStations);
      });
  }

  private formatAddressToString(address: Address): string {
    const commonAddressModel = new CommonAddressModel(
      address.city,
      address.postalCode,
      address.road,
      address.number,
      address.letter,
      undefined,
      undefined,
      undefined
    );
    return this.addressService.formatAddressToString(commonAddressModel);
  }
}
